import { Settings } from './../../core/settings';
import { LabTestResult } from '../../lab-test-results/lab-test-result';
import { publishReplay, refCount, map, filter as rxFilter, withLatestFrom, tap } from 'rxjs/operators';
import { LabResultsFilterStorage } from './lab-results-filter-storage';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { LabResultsDataStorage } from './lab-results-data-storage';
import { OsrLegendDataService } from '../../charts/osr-legend-data.service';
import { TrendsChartDataBuilder, LabTrendsChartDataValue } from '../../charts/trends-chart-data-builder';
import { ChartLegendItem } from '../../legend';
import { CvdBandDataValue, CvdChartData, ICvdChartEvent, AfterChartRenderedEventArgs } from '@covance/ng-cvd3';
import { ScaleLinear, select, Selection } from 'd3';
import { Observable, Subject, Subscription } from 'rxjs';
import { FlaggedClassNames, canBeUsedInTrends, hideAllButSelectedSubjects, opaqueAllButSelectedSubjects, removeOpacityFromAllElements } from '../../charts/chart-helper';
import { BandRenderer } from '../../charts/band-renderer';
import { UrlBuilder } from '../../core';
import { SponsorRouteData } from '../sponsor-route-data';
import { removeAllTooltips } from '../../core/tooltip-remover';
import { CovanceFilter } from '../../covance-filter';
import { isEmpty } from 'lodash';
import { LabResultSponsorTooltipRenderer } from './lab-result-tooltip-renderer';

@Component({
    selector: 'sponsor-trends',
    template: require('./sponsor-trends.component.html'),
    styles: [require('./../../on-screen-results/standard-test-results/lab-chart.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LabResultSponsorTooltipRenderer],
})
export class SponsorTrendsComponent implements OnDestroy {
    public bandData: Observable<CvdChartData<CvdBandDataValue>[]>;
    public sexLegendData: Observable<ChartLegendItem[]>;
    public subjectLegendData: Observable<ChartLegendItem[]>;
    public flaggedLegendData: Observable<ChartLegendItem[]>;
    public lineChartData: Observable<CvdChartData<LabTrendsChartDataValue>[]>;
    public yAxisTitle: Observable<string>;

    public tooltip = this.labResultSponsorTooltipRenderer.renderer;
    public alertMessage: string;

    private xScale: ScaleLinear<number, number>;
    private yScale: ScaleLinear<number, number>;
    private selectedTestIds: number[];
    private svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any> = null;
    private data: LabTestResult[]; 
    private readonly selectionDone = new Subject();
    private readonly subscription: Subscription;
    
    constructor(
        settings: Settings,
        private storage: LabResultsDataStorage,
        private legendDataService: OsrLegendDataService,
        private chartDataBuilder: TrendsChartDataBuilder,
        private bandRenderrer: BandRenderer,
        private labResultSponsorTooltipRenderer: LabResultSponsorTooltipRenderer,
        private urlBuilder: UrlBuilder,
        private sponsorRoutes: SponsorRouteData,
        filterStorage: LabResultsFilterStorage,
        filter: CovanceFilter,
    ) { 
        const testData = storage.labTestsData.pipe(
            map(results => results && results.filter(r => canBeUsedInTrends(r))),
            map(results => {
                if (!results) {
                    return results;
                }
                const uniqSubjects = new Set<string>();
                this.alertMessage = null;
                results.forEach( r => uniqSubjects.add(r.zcSubjectId));
                if (uniqSubjects.size > settings.maxNumberOfTrends) {
                    this.alertMessage = 'Too many trends to render. Please use the filters to reduce the number of trends.';
                    return [];
                }
                return results;
            }),
            tap(d => this.data = d),
            publishReplay(1), 
            refCount()
        );

        this.lineChartData = testData.pipe(
                map(lr => this.buildChartData(lr)),
                publishReplay(1), 
                refCount()
            );

        this.yAxisTitle = testData.pipe(
            rxFilter(td => !isEmpty(td)),
            map(tr => tr[0].testNameWithUnit),
            publishReplay(1), 
            refCount()
        );

        this.bandData = testData.pipe(map(lr => this.chartDataBuilder.buildBandData(lr)));
        this.sexLegendData = testData.pipe(map(lr => this.legendDataService.buildSexLegendData(lr)));
        this.subjectLegendData = testData.pipe(map(lr =>  this.legendDataService.buildSubjectLegendData(lr)));
        this.flaggedLegendData = testData.pipe(map(lr => this.legendDataService.buildFlaggedLegendData(lr)));

        this.subscription = this.selectionDone
            .pipe(
                withLatestFrom(filterStorage.subjectFilter)
            ).subscribe(([_, subjectFilter]) => {
                if (isEmpty(this.selectedTestIds)) {
                    return;
                }

                var subjectsQueryParams = subjectFilter.def.getItems(this.selectedTestIds.map(lr => this.storage.testsDataSnapshotDictionary[lr]));
                filter.setValue(subjectFilter, subjectsQueryParams);
            });
    }

    public goToSubject(event: ICvdChartEvent<LabTrendsChartDataValue>) {
        const labTestId = event.data.rows[0];
        const labTestResult = this.storage.testsDataSnapshotDictionary[labTestId];

        if (!labTestResult.zcSubjectId) {
            return;
        }

        this.urlBuilder
            .esa()
            .sponsor()
            .study(this.sponsorRoutes.snapshot.selectedStudy)
            .subjects(labTestResult.zcSubjectId)
            .navigate();
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
        removeAllTooltips();
    }

    public afterChartRendered(event: AfterChartRenderedEventArgs) {

        if (this.svgChart === null) {
            this.svgChart = select<SVGElement, CvdChartData<LabTrendsChartDataValue>[]>('#' + event.svgId);
            this.svgChart.on('mouseup', () => {
                this.selectionDone.next(); 
            });
        }

        this.xScale = event.xScale;
        this.yScale = event.yScale;

        this.bandRenderrer.initHovers(event.svgId, this.xScale, this.yScale, this.storage.testsDataSnapshotDictionary);

        this.setPointsColors(this.svgChart);

        const areaSvg = this.svgChart.select<SVGElement>('.cv-x.cv-axis');
        const rect = areaSvg.node().getBoundingClientRect();

        const ticks = areaSvg.selectAll<SVGElement, number>('.tick text');
        ticks.attr('hidden', function () {
            const tickRect = this.getBoundingClientRect();
            return (tickRect.left === rect.left || tickRect.right === rect.right) ? true : null;
        });
    }

    public filterSubjects(selectedItems: number[]) {
        this.selectedTestIds = selectedItems;
    }

    public subjectsSelected(selectedItems: ChartLegendItem[]) {
        hideAllButSelectedSubjects(this.svgChart, selectedItems, this.data);
    }

    public subjectLegendMouseOver(selectedItem: ChartLegendItem) {
        opaqueAllButSelectedSubjects(this.svgChart, selectedItem, this.data);
    }

    public subjectLegendMouseOut() {
        removeOpacityFromAllElements(this.svgChart);
    }

    protected buildChartData(data: any[]): CvdChartData<LabTrendsChartDataValue>[] {
        const cvdData = this.chartDataBuilder.buildChartData(data);
        return cvdData;
    }

    private setPointsColors(svgChart: Selection<SVGElement, CvdChartData<LabTrendsChartDataValue>[], HTMLElement, any>) {
        svgChart.selectAll<SVGPathElement, LabTrendsChartDataValue>('.cv-scatter g.cv-groups g.cv-group path.cv-point')
            .classed(FlaggedClassNames.high, false)
            .classed(FlaggedClassNames.low, false)
            .classed(FlaggedClassNames.other, false)
            .classed(FlaggedClassNames.nonFlagged, false)
            .each(function (data) {
                select(this).classed(data.colorClassName, true);
            });
    }
}
